import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { API, API_BASE_URL } from "../config/api";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { FileText, Check, X, Clock, Loader2, Plus, Send, Download, Upload, Trash2, Eye, FileImage, File } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";


export default function DocumentRequestsPage() {
  const { user, getAuthHeader } = useAuth();
  const [requests, setRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveFiles, setApproveFiles] = useState([]);
  const [cases, setCases] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [historyFilter, setHistoryFilter] = useState("all"); // all, pending, approved, rejected
  
  const [formData, setFormData] = useState({
    case_id: "",
    document_name: "",
    description: "",
  });

  const isAdmin = user?.role === "admin";
  
  // Helper to check if file is previewable
  const isPreviewable = (filename) => {
    const ext = filename?.toLowerCase().split('.').pop();
    return ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
  };
  
  const isPdf = (filename) => {
    return filename?.toLowerCase().endsWith('.pdf');
  };
  
  const isImage = (filename) => {
    const ext = filename?.toLowerCase().split('.').pop();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
  };
  
  const openPreview = (doc) => {
    setPreviewDoc(doc);
    setPreviewDialogOpen(true);
  };

  const fetchData = useCallback(async () => {
    try {
      // Fetch pending requests (for admin)
      if (isAdmin) {
        const response = await axios.get(`${API}/document-requests`, getAuthHeader());
        setRequests(response.data);
      }
      
      // Fetch my requests (for team members)
      const myResponse = await axios.get(`${API}/document-requests/my`, getAuthHeader());
      setMyRequests(myResponse.data);
      
      // Fetch cases for dropdown
      const casesResponse = await axios.get(`${API}/cases`, getAuthHeader());
      setCases(casesResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (requestId) => {
    // Find the request and open the approve dialog
    const request = requests.find(r => r.id === requestId);
    setSelectedRequest(request);
    setApproveFiles([]);
    setApproveDialogOpen(true);
  };

  const handleApproveFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setApproveFiles(prev => [...prev, ...newFiles]);
  };

  const removeApproveFile = (index) => {
    setApproveFiles(prev => prev.filter((_, i) => i !== index));
  };

  const submitApproval = async () => {
    if (!selectedRequest) return;
    
    setProcessing(selectedRequest.id);
    try {
      const formData = new FormData();
      approveFiles.forEach(file => {
        formData.append('files', file);
      });

      await axios.post(
        `${API}/document-requests/${selectedRequest.id}/approve`,
        formData,
        {
          ...getAuthHeader(),
          headers: {
            ...getAuthHeader().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const docCount = approveFiles.length;
      toast.success(docCount > 0 
        ? `Cererea a fost aprobată cu ${docCount} document(e) atașat(e)` 
        : "Cererea a fost aprobată"
      );
      setApproveDialogOpen(false);
      setSelectedRequest(null);
      setApproveFiles([]);
      fetchData();
    } catch (error) {
      toast.error("Eroare la aprobarea cererii");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.post(`${API}/document-requests/${requestId}/reject`, {}, getAuthHeader());
      toast.success("Cererea a fost respinsă");
      fetchData();
    } catch (error) {
      toast.error("Eroare la respingerea cererii");
    } finally {
      setProcessing(null);
    }
  };

  // Selection handlers
  const toggleSelectRequest = (requestId) => {
    setSelectedIds(prev => 
      prev.includes(requestId) 
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  const toggleSelectAll = (requestsList) => {
    if (selectedIds.length === requestsList.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(requestsList.map(r => r.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    setDeleting(true);
    try {
      await axios.post(`${API}/document-requests/bulk-delete`, selectedIds, getAuthHeader());
      toast.success(`${selectedIds.length} cereri șterse`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error("Eroare la ștergerea cererilor");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSingle = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.delete(`${API}/document-requests/${requestId}`, getAuthHeader());
      toast.success("Cererea a fost ștearsă");
      setSelectedIds(prev => prev.filter(id => id !== requestId));
      fetchData();
    } catch (error) {
      toast.error("Eroare la ștergerea cererii");
    } finally {
      setProcessing(null);
    }
  };

  // Filter requests based on history filter
  const filteredRequests = requests.filter(r => {
    if (historyFilter === "all") return true;
    return r.status === historyFilter;
  });

  const pendingRequests = requests.filter(r => r.status === "pending");
  const historyRequests = requests.filter(r => r.status !== "pending");

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    
    if (!formData.document_name.trim()) {
      toast.error("Introduceți numele documentului solicitat");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/document-requests`,
        formData,
        getAuthHeader()
      );
      toast.success("Cererea a fost trimisă. Veți fi notificat când este aprobată.");
      setDialogOpen(false);
      setFormData({ case_id: "", document_name: "", description: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la trimiterea cererii");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20">
            <Clock className="w-3 h-3 mr-1" />
            În așteptare
          </Badge>
        );
      case "approved":
        return (
          <Badge variant="outline" className="bg-success/10 text-success border-success/20">
            <Check className="w-3 h-3 mr-1" />
            Aprobată
          </Badge>
        );
      case "rejected":
        return (
          <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20">
            <X className="w-3 h-3 mr-1" />
            Respinsă
          </Badge>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse font-body text-muted-foreground">Se încarcă cererile...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="document-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Cereri Documente</h1>
          <p className="font-body text-muted-foreground mt-1">
            {isAdmin ? "Gestionează cererile de acces la documente" : "Solicită documente de la administrator"}
          </p>
        </div>
        {!isAdmin && (
          <Button onClick={() => setDialogOpen(true)} data-testid="new-request-btn">
            <Plus className="w-4 h-4 mr-2" />
            Cerere Nouă
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Requests - Admin Only */}
        {isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Clock className="w-5 h-5 text-warning" />
                Cereri în Așteptare ({pendingRequests.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingRequests.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="font-body text-muted-foreground">Nu există cereri în așteptare.</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {pendingRequests.map((request) => (
                      <div
                        key={request.id}
                        className="p-4 rounded-lg border bg-card"
                        data-testid={`request-${request.id}`}
                      >
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            {getStatusBadge(request.status)}
                            <span className="font-mono text-xs text-muted-foreground">
                              {format(new Date(request.created_at), "dd/MM/yyyy HH:mm")}
                            </span>
                          </div>
                          <p className="font-body font-medium">{request.user_name}</p>
                          <div className="p-2 bg-muted rounded-md">
                            <p className="font-body text-sm font-medium">{request.document_name}</p>
                            {request.description && (
                              <p className="font-body text-xs text-muted-foreground mt-1">
                                {request.description}
                              </p>
                            )}
                            {request.case_no && (
                              <p className="font-mono text-xs text-muted-foreground mt-1">
                                Dosar: {request.case_no}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApprove(request.id)}
                              disabled={processing === request.id}
                              className="flex-1 bg-success hover:bg-success/90"
                            >
                              {processing === request.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Check className="w-4 h-4 mr-1" />
                                  Aprobă
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleReject(request.id)}
                              disabled={processing === request.id}
                              className="flex-1"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Respinge
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        )}

        {/* History - Admin Only */}
        {isAdmin && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Istoric Cereri ({historyRequests.length})
                </CardTitle>
                {selectedIds.length > 0 && (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleBulkDelete}
                    disabled={deleting}
                    data-testid="bulk-delete-btn"
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-1" />
                    )}
                    Șterge ({selectedIds.length})
                  </Button>
                )}
              </div>
              {/* Filter tabs */}
              <div className="flex gap-2 mt-3">
                {[
                  { value: "all", label: "Toate" },
                  { value: "approved", label: "Aprobate" },
                  { value: "rejected", label: "Respinse" },
                ].map((filter) => (
                  <Button
                    key={filter.value}
                    size="sm"
                    variant={historyFilter === filter.value ? "default" : "outline"}
                    onClick={() => setHistoryFilter(filter.value)}
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {historyRequests.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="font-body text-muted-foreground">Nu există cereri în istoric.</p>
                </div>
              ) : (
                <ScrollArea className="h-[350px]">
                  {/* Select all checkbox */}
                  {historyRequests.filter(r => historyFilter === "all" || r.status === historyFilter).length > 0 && (
                    <div className="flex items-center gap-2 pb-3 border-b mb-3">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300"
                        checked={selectedIds.length === historyRequests.filter(r => historyFilter === "all" || r.status === historyFilter).length && selectedIds.length > 0}
                        onChange={() => toggleSelectAll(historyRequests.filter(r => historyFilter === "all" || r.status === historyFilter))}
                      />
                      <span className="font-body text-sm text-muted-foreground">Selectează tot</span>
                    </div>
                  )}
                  <div className="space-y-3">
                    {historyRequests
                      .filter(r => historyFilter === "all" || r.status === historyFilter)
                      .map((request) => (
                      <div
                        key={request.id}
                        className={`p-3 rounded-lg border bg-card ${selectedIds.includes(request.id) ? 'ring-2 ring-primary' : ''}`}
                        data-testid={`history-request-${request.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            className="w-4 h-4 mt-1 rounded border-gray-300"
                            checked={selectedIds.includes(request.id)}
                            onChange={() => toggleSelectRequest(request.id)}
                          />
                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              {getStatusBadge(request.status)}
                              <span className="font-mono text-xs text-muted-foreground">
                                {format(new Date(request.created_at), "dd/MM/yyyy")}
                              </span>
                            </div>
                            <p className="font-body text-sm">{request.user_name}</p>
                            <p className="font-body text-sm font-medium truncate">{request.document_name}</p>
                            {request.documents && request.documents.length > 0 && (
                              <p className="font-mono text-xs text-success">
                                {request.documents.length} document(e) atașat(e)
                              </p>
                            )}
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleDeleteSingle(request.id)}
                            disabled={processing === request.id}
                          >
                            {processing === request.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        )}

        {/* My Requests - Team Members */}
        {!isAdmin && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Cererile Mele ({myRequests.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {myRequests.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="font-body text-muted-foreground">
                    Nu ați trimis nicio cerere încă.
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setDialogOpen(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Trimite Prima Cerere
                  </Button>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {myRequests.map((request) => (
                      <div
                        key={request.id}
                        className="p-3 rounded-lg border bg-card"
                        data-testid={`my-request-${request.id}`}
                      >
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              {getStatusBadge(request.status)}
                            </div>
                            <p className="font-body font-medium">{request.document_name}</p>
                            {request.case_no && (
                              <p className="font-mono text-xs text-muted-foreground">
                                Dosar: {request.case_no}
                              </p>
                            )}
                            <p className="font-mono text-xs text-muted-foreground">
                              {format(new Date(request.created_at), "dd/MM/yyyy HH:mm")}
                            </p>
                          </div>
                        </div>
                        
                        {/* Documents section for approved requests */}
                        {request.status === "approved" && request.documents && request.documents.length > 0 && (
                          <div className="mt-2 pt-2 border-t space-y-2">
                            <p className="font-body text-xs text-muted-foreground flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              Documente disponibile ({request.documents.length}):
                            </p>
                            <div className="space-y-2">
                              {request.documents.map((doc, idx) => (
                                <div key={idx} className="flex items-center gap-2 p-2 bg-secondary/50 rounded-md">
                                  {isImage(doc.filename) ? (
                                    <FileImage className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                  ) : isPdf(doc.filename) ? (
                                    <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                                  ) : (
                                    <File className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                  )}
                                  <span className="font-body text-sm flex-1 truncate">{doc.filename}</span>
                                  <div className="flex gap-1">
                                    {isPreviewable(doc.filename) && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 px-2"
                                        onClick={() => openPreview(doc)}
                                        data-testid={`preview-doc-${idx}`}
                                      >
                                        <Eye className="w-3 h-3 mr-1" />
                                        Previzualizare
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 px-2"
                                      onClick={() => window.open(`${API_BASE_URL}${doc.url}`, '_blank')}
                                      data-testid={`download-doc-${idx}`}
                                    >
                                      <Download className="w-3 h-3 mr-1" />
                                      Descarcă
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Legacy single document support */}
                        {request.status === "approved" && !request.documents && request.document_url && (
                          <div className="mt-2 pt-2 border-t">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`${API_BASE_URL}${request.document_url}`, '_blank')}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Descarcă Document
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {/* New Request Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="new-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Solicită Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitRequest} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="document_name">Nume Document *</Label>
              <Input
                id="document_name"
                value={formData.document_name}
                onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
                placeholder="Ex: Contract de vânzare-cumpărare"
                data-testid="input-document-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="case_id">Dosar Asociat (opțional)</Label>
              <Select
                value={formData.case_id}
                onValueChange={(value) => setFormData({ ...formData, case_id: value })}
              >
                <SelectTrigger data-testid="select-case">
                  <SelectValue placeholder="Selectează dosarul" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Fără dosar --</SelectItem>
                  {cases.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.numar_dosar} - {c.numar_dosar_intern}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Descriere / Motiv</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrieți de ce aveți nevoie de acest document..."
                rows={3}
                data-testid="input-description"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Anulează
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Trimite Cererea
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Approve Request Dialog with File Upload */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="approve-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Aprobă Cererea</DialogTitle>
            <DialogDescription>
              Încarcă documentele solicitate pentru a le trimite către {selectedRequest?.user_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Request Details */}
            <div className="p-3 bg-muted rounded-lg space-y-2">
              <p className="font-body text-sm">
                <span className="text-muted-foreground">Document solicitat:</span>{" "}
                <strong>{selectedRequest?.document_name}</strong>
              </p>
              {selectedRequest?.description && (
                <p className="font-body text-sm">
                  <span className="text-muted-foreground">Motiv:</span>{" "}
                  {selectedRequest.description}
                </p>
              )}
              {selectedRequest?.case_no && (
                <p className="font-mono text-xs text-muted-foreground">
                  Dosar: {selectedRequest.case_no}
                </p>
              )}
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Încarcă Documente</Label>
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                <Input
                  type="file"
                  multiple
                  onChange={handleApproveFileChange}
                  className="hidden"
                  id="approve-file-input"
                  data-testid="approve-file-input"
                />
                <label htmlFor="approve-file-input" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="font-body text-sm text-muted-foreground">
                    Click pentru a selecta fișiere sau trage aici
                  </p>
                  <p className="font-mono text-xs text-muted-foreground mt-1">
                    PDF, DOC, DOCX, JPG, PNG
                  </p>
                </label>
              </div>
            </div>

            {/* Selected Files List */}
            {approveFiles.length > 0 && (
              <div className="space-y-2">
                <Label>Fișiere selectate ({approveFiles.length})</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {approveFiles.map((file, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-2 bg-secondary rounded-md"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span className="font-body text-sm truncate">{file.name}</span>
                        <span className="font-mono text-xs text-muted-foreground flex-shrink-0">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-destructive hover:text-destructive"
                        onClick={() => removeApproveFile(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setApproveDialogOpen(false);
                setSelectedRequest(null);
                setApproveFiles([]);
              }}
            >
              Anulează
            </Button>
            <Button 
              onClick={submitApproval}
              disabled={processing === selectedRequest?.id}
              className="bg-success hover:bg-success/90"
              data-testid="submit-approve-btn"
            >
              {processing === selectedRequest?.id ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {approveFiles.length > 0 
                ? `Aprobă și Trimite ${approveFiles.length} Document(e)`
                : "Aprobă fără Documente"
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col" data-testid="preview-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-heading flex items-center gap-2">
              {previewDoc && isImage(previewDoc.filename) ? (
                <FileImage className="w-5 h-5 text-blue-500" />
              ) : (
                <FileText className="w-5 h-5 text-red-500" />
              )}
              {previewDoc?.filename}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 min-h-0 bg-muted rounded-lg overflow-hidden">
            {previewDoc && isPdf(previewDoc.filename) && (
              <iframe
                src={`${API_BASE_URL}${previewDoc.url}`}
                className="w-full h-full border-0"
                title={previewDoc.filename}
              />
            )}
            {previewDoc && isImage(previewDoc.filename) && (
              <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                <img
                  src={`${API_BASE_URL}${previewDoc.url}`}
                  alt={previewDoc.filename}
                  className="max-w-full max-h-full object-contain rounded-md shadow-lg"
                />
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-shrink-0 gap-2">
            <Button
              variant="outline"
              onClick={() => setPreviewDialogOpen(false)}
            >
              Închide
            </Button>
            <Button
              onClick={() => window.open(`${API_BASE_URL}${previewDoc?.url}`, '_blank')}
            >
              <Download className="w-4 h-4 mr-2" />
              Descarcă
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
